import React, { Fragment, useEffect, useState } from 'react'
import './Dashboard.scss'

import totalUsersIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Dashboard/total-users.svg'
import activeStationsIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Dashboard/active-stations.svg'
import walletFloatIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Dashboard/wallet-float.svg'
import todaysVolumeIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Dashboard/todays-volume.svg'
import chevronDownIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Dashboard/chevron-down.svg'
import { timelineBXService, TimelineItem } from '../../../../../Services/timelineBXService'
import { transactionBXService } from '../../../../../Services/transactionBXService'
import { dashboardBXService, DashboardMetrics } from '../../../../../Services/dashboardBXService'
import { TransactionXModel } from 'BoostXpressAdmin/Model/transactionXModel'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'
import { useAuth } from 'Components/AuthProvider/AuthProvider'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

// Helper component for Stat Cards
const StatCard = ({ title, value, change, color, iconBg, icon }: any) => (
  <div className={`bx-stat-card bx-stat-card--${color}`}>
    <div className='bx-stat-card__content'>
      <div className='bx-stat-card__info'>
        <p className='bx-stat-card__title'>{title}</p>
        <h3 className='bx-stat-card__value'>{value}</h3>
        {/* <p
          className={`bx-stat-card__change bx-stat-card__change--${change.startsWith('↓') ? 'down' : 'up'}`}
        >
          {change}
        </p> */}
      </div>
      <div className='bx-stat-card__icon-wrapper' style={{ backgroundColor: iconBg }}>
        <img src={icon} alt={title} className='bx-stat-card__icon' />
      </div>
    </div>
  </div>
)

const Dashboard = () => {
  const { user } = useAuth()
  const [timelines, setTimelines] = useState<TimelineItem[]>([])
  const [isLoadingTimelines, setIsLoadingTimelines] = useState(false)
  const [transactions, setTransactions] = useState<TransactionXModel[]>([])
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false)

  // Dashboard metrics state
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false)

  // Filter state
  const [filterPeriod, setFilterPeriod] = useState<'week' | 'month' | 'year'>('week')

  

  const formatDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
  
    return `${year}-${month}-${day}`
  }
  
  const getDateRange = (period: 'week' | 'month' | 'year') => {
    const now = new Date()
  
    let startDate: Date
    let endDate: Date
  
    switch (period) {
      case 'week': {
        const dayOfWeek = now.getDay()  
  
        // Start of week (Sunday)
        startDate = new Date(now)
        startDate.setDate(now.getDate() - dayOfWeek)
  
        // End of week (Saturday)
        endDate = new Date(startDate)
        endDate.setDate(startDate.getDate() + 6)
        break
      }
  
      case 'month':
        // First day of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
  
        // Last day of current month
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        break
  
      case 'year':
        // First day of current year
        startDate = new Date(now.getFullYear(), 0, 1)
  
        // Last day of current year
        endDate = new Date(now.getFullYear(), 11, 31)
        break
    }
  
    return {
      'startDateYYYY_MM_DD': formatDate(startDate),
      'endDateYYYY_MM_DD': formatDate(endDate),
    }
  }

  // Aggregate daily data into monthly data for yearly view
  const aggregateYearlyData = (data: Array<{ date: string; value: number }>) => {
    const monthlyData: { [key: string]: number } = {}

    data.forEach((item) => {
      const date = new Date(item.date)
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      monthlyData[monthKey] = (monthlyData[monthKey] || 0) + item.value
    })

    // Get all months in order
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ]

    const currentYear = new Date().getFullYear()
    return months.map((month) => {
      const monthKey = `${month} ${currentYear}`
      return monthlyData[monthKey] || 0
    })
  }

  // Get formatted labels based on filter period
  const getChartLabels = (period: 'week' | 'month' | 'year') => {
    if (!metrics?.transactionVolumeGraph.length)
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

    if (period === 'year') {
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    }

    return metrics.transactionVolumeGraph.map((item) => {
      const date = new Date(item.date)
      switch (period) {
        case 'week':
          return date.toLocaleDateString('en-US', { weekday: 'short' })
        case 'month':
          return date.toLocaleDateString('en-US', { day: 'numeric' })
        default:
          return date.toLocaleDateString('en-US', { weekday: 'short' })
      }
    })
  }

  // Transaction Volume Chart Data - using real metrics and dynamic labels
  const transactionVolumeLabels = getChartLabels(filterPeriod)

  const transactionVolumeChartData = filterPeriod === 'year'
    ? aggregateYearlyData(metrics?.transactionVolumeGraph || [])
    : metrics?.transactionVolumeGraph.map((item) => item.value) || [43, 85, 74, 55, 63, 75, 85]

  const transactionVolumeData = {
    labels: transactionVolumeLabels,
    datasets: [
      {
        data: transactionVolumeChartData,
        backgroundColor: '#E873A8',
        borderRadius: 4,
        barThickness: filterPeriod === 'year' ? 24 : 48,
      },
    ],
  }

  // User Registration Chart Data - using real metrics and dynamic labels
  const userRegistrationLabels = getChartLabels(filterPeriod)

  const userRegistrationChartData = filterPeriod === 'year'
    ? aggregateYearlyData(metrics?.userRegistrationGraph || [])
    : metrics?.userRegistrationGraph.map((item) => item.value) || [200, 220, 240, 260, 280, 300, 320]

  const userRegistrationData = {
    labels: userRegistrationLabels,
    datasets: [
      {
        data: userRegistrationChartData,
        borderColor: '#3378f3',
        backgroundColor: 'rgba(51, 120, 243, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#3378f3',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: filterPeriod === 'year' ? 3 : 4,
      },
    ],
  }

  // Fetch dashboard metrics
  const fetchMetrics = async () => {
    try {
      setIsLoadingMetrics(true)
      const dateRange = getDateRange(filterPeriod)
      const response = await dashboardBXService.getMetrics(dateRange)
      // console.log({ getMetrics: response.data.data })
      setMetrics(response.data.data)
    } catch (error) {
      console.error('Failed to fetch dashboard metrics:', error)
      setMetrics(null)
    } finally {
      setIsLoadingMetrics(false)
    }
  }

  // User Registration Chart Options
  const userRegistrationOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#666666',
          font: {
            size: 11,
            family: 'Inter',
          },
        },
        grid: {
          color: '#F2F4F7',
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: '#666666',
          font: {
            size: 11,
            family: 'Inter',
          },
        },
      },
    },
  }

  const transactionVolumeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context: any) => {
            return `${context.parsed.y.toLocaleString()} litres`
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => {
            if (value >= 1000) return `${(value / 1000).toFixed(1)}k`
            return value
          },
          color: '#666666',
          font: {
            size: 11,
            family: 'Inter',
          },
        },
        grid: {
          color: '#F2F4F7',
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: '#666666',
          font: {
            size: 11,
            family: 'Inter',
          },
        },
      },
    },
  }

  useEffect(() => {
    const fetchTimelines = async () => {
      try {
        setIsLoadingTimelines(true)
        const response = await timelineBXService.index({
          page: 1,
          limit: 7,
          sortOrder: 'DESC',
        })
        setTimelines(response.data?.data || [])
      } catch (error) {
        console.error('Failed to fetch timelines:', error)
        setTimelines([])
      } finally {
        setIsLoadingTimelines(false)
      }
    }

    const fetchTransactions = async () => {
      try {
        setIsLoadingTransactions(true)
        const response = await transactionBXService.index({
          page: 1,
          limit: 7,
          sortOrder: 'DESC',
        })
        setTransactions(response.data?.data || [])
      } catch (error) {
        console.error('Failed to fetch transactions:', error)
        setTransactions([])
      } finally {
        setIsLoadingTransactions(false)
      }
    }

    fetchTimelines()
    fetchTransactions()
    fetchMetrics()
  }, [])

  // Refetch metrics when filter period changes
  useEffect(() => {
    fetchMetrics()
  }, [filterPeriod])

  const formatTimelineText = (timeline: TimelineItem) => {
    const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')
    const user = timeline.action_by_user
    const name = [text(user?.firstName), text(user?.lastName)].filter(Boolean).join(' ')
    const actor =
      timeline.actor_type === 'admin' ? 'Admin' : timeline.actor_type === 'system' ? 'System' : 'User'
    const email =
      text(timeline.metadata?.actorEmail) || text(timeline.metadata?.adminEmail) || text(user?.email)
    const userName = name || (email ? `${actor} [${email}]` : actor)

    switch (timeline.action) {
      case 'user_registered':
        return (
          <>
            New user registered: <strong>{userName}</strong>
          </>
        )
      case 'station_offline':
        return (
          <>
            Station <strong>{timeline.note || 'Unknown Station'}</strong> went offline
          </>
        )
      case 'ticket_resolved':
        return (
          <>
            Ticket #{timeline.about_id} resolved by <strong>{userName}</strong>
          </>
        )
      case 'payment_failed':
        return (
          <>
            Failed funding attempt — <strong>{timeline.note || 'Amount reversal'}</strong>
          </>
        )
      default:
        return (
          <>
            <strong>{userName}</strong> {timeline.action} {timeline.note}
          </>
        )
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'user_registered':
        return '#00c12b'
      case 'station_offline':
        return '#f0973d'
      case 'ticket_resolved':
        return '#3378f3'
      case 'payment_failed':
        return '#ff0000'
      default:
        return '#6c757d'
    }
  }

  const formatTimeAgo = (createdAt: string) => {
    const now = new Date()
    const created = new Date(createdAt)
    const diffMs = now.getTime() - created.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  const firstName =
    user?.user?.firstName || user?.user?.name?.split(' ')[0] || user?.name?.split(' ')[0] || 'Admin'

  return (
    <div className='bx-dashboard'>
      <header className='bx-dashboard__header'>
        <div className='bx-dashboard__greeting'>
          <h2 className='bx-dashboard__greeting-title'>Good morning, {firstName} 👋</h2>
          <p className='bx-dashboard__greeting-subtitle'>
            Here&lsquo;s what&lsquo;s happening on the platform today
          </p>
        </div>
      </header>

      <div className='bx-dashboard__stats'>
        <StatCard
          title='Total Users'
          value={isLoadingMetrics ? 'Loading...' : metrics?.totalUsers.toLocaleString() || '0'}
          change={`↑ ${metrics?.totalNewUsersInRange || 0} new users`}
          color='blue'
          iconBg='rgba(51, 120, 243, 0.15)'
          icon={totalUsersIcon}
        />

        <StatCard
          title='Active Stations'
          value={
            isLoadingMetrics ? 'Loading...' : metrics?.totalActiveStations.toLocaleString() || '0'
          }
          change='Active stations'
          color='green'
          iconBg='rgba(46, 204, 113, 0.15)'
          icon={activeStationsIcon}
        />
        <StatCard
          title='Total Volume'
          value={
            isLoadingMetrics
              ? 'Loading...'
              : `${(metrics?.totalVolumeInRange || 0).toFixed(2)} litres`
          }
          change='Total volume in range'
          color='purple'
          iconBg='rgba(134, 61, 229, 0.15)'
          icon={walletFloatIcon}
        />
        <StatCard
          title="Today's Volume"
          value={
            isLoadingMetrics
              ? 'Loading...'
              : `${(metrics?.totalVolumeToday || (metrics as any)?.todayVolume || 0).toFixed(2)} litres`
          }
          change="Today's transaction volume"
          color='orange'
          iconBg='rgba(240, 151, 61, 0.15)'
          icon={todaysVolumeIcon}
        />
      </div>

      <div className='bx-dashboard__charts'>
        <div className='bx-dashboard__card bx-dashboard__card--chart'>
          <div className='bx-dashboard__card-header'>
            <h4 className='bx-dashboard__card-title'>Transaction Volume</h4>
            <div className='bx-dashboard__card-filter'>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value as 'week' | 'month' | 'year')}
                className='bx-filter-select'
              >
                <option value='week'>This Week</option>
                <option value='month'>This Month</option>
                <option value='year'>This Year</option>
              </select>
              <img src={chevronDownIcon} alt='Filter' className='bx-icon-chevron' />
            </div>
          </div>
          <div className='bx-dashboard__chart-content'>
            <div style={{ height: '300px', width: '100%' }}>
              <Bar data={transactionVolumeData} options={transactionVolumeOptions} />
            </div>
          </div>
        </div>

        <div className='bx-dashboard__card bx-dashboard__card--chart'>
          <div className='bx-dashboard__card-header'>
            <h4 className='bx-dashboard__card-title'>User Registrations</h4>
            <div className='bx-dashboard__card-filter'>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value as 'week' | 'month' | 'year')}
                className='bx-filter-select'
              >
                <option value='week'>This Week</option>
                <option value='month'>This Month</option>
                <option value='year'>This Year</option>
              </select>
              <img src={chevronDownIcon} alt='Filter' className='bx-icon-chevron' />
            </div>
          </div>
          <div className='bx-dashboard__chart-content'>
            <div style={{ height: '300px', width: '100%' }}>
              <Line data={userRegistrationData} options={userRegistrationOptions} />
            </div>
          </div>
        </div>
      </div>

      <div className='bx-dashboard__bottom'>
        <div className='bx-dashboard__card bx-dashboard__card--table'>
          <div className='bx-dashboard__card-header'>
            <h4 className='bx-dashboard__card-title'>Recent Transactions</h4>
            <button className='bx-dashboard__card-link'>View all</button>
          </div>
          <div className='bx-table-wrapper'>
            <table className='bx-table'>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingTransactions ? (
                  <tr>
                    <td colSpan={4} className='text-center'>
                      <div className='spinner-border spinner-border-sm me-2' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                      Loading transactions...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className='text-center text-muted'>
                      No recent transactions found
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction: TransactionXModel, i) => (
                    <tr key={transaction.resourceId || i}>
                      <td>
                        {transaction.user
                          ? `${transaction.user.firstName} ${transaction.user.lastName}`
                          : 'Unknown User'}
                      </td>
                      <td>{transaction.transactionType || 'Transaction'}</td>
                      <td>
                        {transaction.amountMajor
                          ? `₦${transaction.amountMajor.toLocaleString()}`
                          : '₦0'}
                      </td>
                      <td>
                        <span
                          className={`bx-badge bx-badge--${(transaction.paymentStatus || 'pending').toLowerCase()}`}
                        >
                          {transaction.paymentStatus || 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className='bx-dashboard__card bx-dashboard__card--activity'>
          <div className='bx-dashboard__card-header'>
            <h4 className='bx-dashboard__card-title'>Live Activity</h4>
          </div>
          <div className='bx-activity-list'>
            {isLoadingTimelines ? (
              <div className='bx-activity-item'>
                <div className='bx-activity-content-wrap'>
                  <div className='bx-activity-dot' style={{ backgroundColor: '#6c757d' }}></div>
                  <div className='bx-activity-content'>
                    <span className='bx-activity-text'>Loading activity...</span>
                    <span className='bx-activity-time'>Just now</span>
                  </div>
                </div>
              </div>
            ) : timelines.length === 0 ? (
              <div className='bx-activity-item'>
                <div className='bx-activity-content-wrap'>
                  <div className='bx-activity-dot' style={{ backgroundColor: '#6c757d' }}></div>
                  <div className='bx-activity-content'>
                    <span className='bx-activity-text'>No recent activity</span>
                    <span className='bx-activity-time'>Just now</span>
                  </div>
                </div>
              </div>
            ) : (
              timelines.map((timeline, i) => (
                <div key={timeline.resourceId} className='bx-activity-item'>
                  <div className='bx-activity-content-wrap'>
                    <div
                      className='bx-activity-dot'
                      style={{ backgroundColor: getActionColor(timeline.action) }}
                    ></div>
                    <div className='bx-activity-content'>
                      <span className='bx-activity-text'>{formatTimelineText(timeline)}</span>
                      <span className='bx-activity-time'>{formatTimeAgo(timeline.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
